<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#app';
import { computed, ref } from 'vue';
import { getCookie } from '~/composables/useCookieManager';
import { useClientsStore } from '~/stores/clients';

const route = useRoute();
const config = useRuntimeConfig();
const auth = useAuth();
const clientsStore = useClientsStore();
const isExpanded = ref(false);
const showAllPermissions = ref(false);

// Only show debug tool in non-production environments
const isProduction = config.public.appEnv === 'production';

// Get current page's required permissions
const pageRequiredPermissions = computed(() => {
  const permissions = route.meta?.auth_permissions || [];
  return Array.isArray(permissions) ? permissions : [permissions];
});

// Get user's global permissions from auth.user
const userGlobalPermissions = computed(() => {
  if (!auth.user) return [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const permissions = (auth.user as any).permissions;
  if (!Array.isArray(permissions)) return [];

  return permissions;
});

// Get selected client ID from cookie
const selectedClientId = computed(() => {
  return (
    getCookie('selected_client_id', false) ||
    clientsStore.selectedClientId ||
    null
  );
});

// Get user's client-specific permissions
const userClientPermissions = computed(() => {
  const clientId = selectedClientId.value;
  if (!clientId) return [];

  return clientsStore.clientPermissions;
});

const allUserPermissions = computed(() => [
  ...userGlobalPermissions.value,
  ...userClientPermissions.value,
]);

// Check if user has access to current page
const hasAccess = computed(() => {
  const requiredPermissions = pageRequiredPermissions.value;
  if (requiredPermissions.length === 0) return true;

  return requiredPermissions.some(required =>
    allUserPermissions.value.includes(required)
  );
});

const displayedGlobalPermissions = computed(() => {
  if (showAllPermissions.value) return userGlobalPermissions.value;
  return userGlobalPermissions.value.filter((p: string) =>
    pageRequiredPermissions.value.includes(p)
  );
});

const displayedClientPermissions = computed(() => {
  if (showAllPermissions.value) return userClientPermissions.value;
  return userClientPermissions.value.filter((p: string) =>
    pageRequiredPermissions.value.includes(p)
  );
});

const hasPermission = (permission: string) => {
  return allUserPermissions.value.includes(permission);
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const toggleShowAll = () => {
  showAllPermissions.value = !showAllPermissions.value;
};
</script>

<template>
  <!-- Only render in non-production environments -->
  <div
    v-if="!isProduction"
    class="role-debug"
    :class="{ expanded: isExpanded }"
  >
    <button class="debug-toggle" @click="toggleExpanded">
      <i class="fas fa-shield-alt" />
      <span v-if="isExpanded">Hide</span>
      <span v-else>Permissions</span>
    </button>

    <div v-if="isExpanded" class="debug-content">
      <div class="debug-section">
        <h6 class="debug-title">Current Page</h6>
        <div class="debug-item">
          <span class="label">Path:</span>
          <code>{{ route.path }}</code>
        </div>
        <div class="debug-item">
          <span class="label">Required:</span>
          <div v-if="pageRequiredPermissions.length > 0" class="role-badges">
            <span
              v-for="permission in pageRequiredPermissions"
              :key="permission"
              class="badge"
              :class="hasPermission(permission) ? 'bg-success' : 'bg-danger'"
            >
              <i
                :class="
                  hasPermission(permission) ? 'fas fa-check' : 'fas fa-times'
                "
              />
              {{ permission }}
            </span>
          </div>
          <span v-else class="text-muted">None (public)</span>
        </div>
        <div class="debug-item">
          <span class="label">Access:</span>
          <span :class="hasAccess ? 'text-success' : 'text-danger'">
            <i :class="hasAccess ? 'fas fa-check' : 'fas fa-times'" />
            {{ hasAccess ? 'Granted' : 'Denied' }}
          </span>
        </div>
      </div>

      <div class="debug-section">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="debug-title mb-0">User Permissions</h6>
          <button class="btn-toggle-all" @click="toggleShowAll">
            {{ showAllPermissions ? 'Show Required Only' : 'Show All' }}
          </button>
        </div>

        <div v-if="displayedGlobalPermissions.length > 0">
          <div class="permission-label">Global</div>
          <div class="role-badges">
            <span
              v-for="permission in displayedGlobalPermissions"
              :key="permission"
              class="badge bg-success"
            >
              {{ permission }}
            </span>
          </div>
        </div>

        <div
          v-if="selectedClientId && displayedClientPermissions.length > 0"
          class="mt-2"
        >
          <div class="permission-label">Client ({{ selectedClientId }})</div>
          <div class="role-badges">
            <span
              v-for="permission in displayedClientPermissions"
              :key="permission"
              class="badge bg-info"
            >
              {{ permission }}
            </span>
          </div>
        </div>

        <div
          v-if="
            !showAllPermissions &&
            displayedGlobalPermissions.length === 0 &&
            displayedClientPermissions.length === 0 &&
            pageRequiredPermissions.length > 0
          "
          class="text-muted"
        >
          No matching permissions for this page
        </div>

        <div
          v-if="
            showAllPermissions &&
            userGlobalPermissions.length === 0 &&
            userClientPermissions.length === 0
          "
          class="text-muted"
        >
          No permissions
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.role-debug {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  background: var(--card-bg, #fff);
  border: 1px solid var(--input-border-color, #e7eaf0);
  border-radius: 12px;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.08),
    0 2px 5px rgba(0, 0, 0, 0.05),
    0 0 0 1px rgba(0, 0, 0, 0.03);
  font-size: 12px;
  max-width: 400px;
  font-family: 'Inter', sans-serif;
}

.debug-toggle {
  background: var(--color-primary, #189ee7);
  color: var(--button-primary-text, #fff);
  border: none;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  transition: background 0.2s;
  font-family: 'Inter', sans-serif;
}

.debug-toggle:hover {
  background: var(--button-primary-bg-hover, #158ecf);
}

.debug-toggle i {
  font-size: 14px;
}

.debug-content {
  padding: 1.25rem;
  max-height: 70vh;
  overflow-y: auto;
}

.debug-section {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--input-border-color, #e7eaf0);
}

.debug-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.debug-title {
  font-weight: 700;
  color: var(--layout-text-heading, #252c35);
  margin-bottom: 0.75rem;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Inter', sans-serif;
}

.debug-item {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.debug-item:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 600;
  color: var(--layout-text-muted, #7c8085);
  min-width: 100px;
  font-size: 11px;
}

.role-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.badge {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  gap: 4px;
}

.badge i {
  font-size: 9px;
}

.btn-toggle-all {
  background: transparent;
  border: 1px solid var(--input-border-color, #e7eaf0);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  cursor: pointer;
  color: var(--layout-text-muted, #7c8085);
  font-weight: 600;
  transition: all 0.2s;
}

.btn-toggle-all:hover {
  background: var(--color-secondary, #f6f8fa);
  color: var(--color-primary, #189ee7);
}

.permission-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--layout-text-muted, #7c8085);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

code {
  background: var(--color-secondary, #f6f8fa);
  padding: 3px 8px;
  border-radius: 6px;
  color: var(--color-primary, #189ee7);
  font-size: 11px;
  font-weight: 500;
  font-family: 'Courier New', monospace;
}

.text-muted {
  color: var(--layout-text-muted, #7c8085);
  font-style: italic;
  font-size: 11px;
}

.text-success {
  color: var(--button-success-bg, #59a96a);
  font-weight: 600;
}

.text-danger {
  color: var(--button-danger-bg, #da2c38);
  font-weight: 600;
}

.fw-bold {
  font-weight: 700;
}
</style>
